import type { ReactNode } from "react";

function H2({ children }: { children: ReactNode }) {
	return (
		<h2
			className="mt-10 font-mono text-[clamp(1.15rem,2.2vw,1.4rem)] leading-[1.2] tracking-[-0.01em] text-foreground"
			style={{ fontWeight: 400 }}
		>
			{children}
		</h2>
	);
}

function H3({ children }: { children: ReactNode }) {
	return (
		<h3 className="mt-7 font-mono text-base leading-[1.3] text-foreground">
			{children}
		</h3>
	);
}

function P({ children }: { children: ReactNode }) {
	return (
		<p className="mt-4 text-base leading-[1.65] text-muted-foreground">
			{children}
		</p>
	);
}

function A({ href, children }: { href: string; children: ReactNode }) {
	return (
		<a
			href={href}
			className="text-foreground underline underline-offset-4 transition-colors hover:text-brand"
		>
			{children}
		</a>
	);
}

function Note({ children }: { children: ReactNode }) {
	return (
		<div className="mt-6 border border-border bg-card p-4">
			<p className="text-base leading-[1.6] text-muted-foreground">
				{children}
			</p>
		</div>
	);
}

const ROWS: Array<{
	tool: string;
	price: string;
	engines: string;
	fit: string;
}> = [
	{
		tool: "Otterly.AI",
		price: "$29/mo Lite, $189 Standard, $489 Premium",
		engines: "4 core, others as paid add-ons",
		fit: "Cheapest real entry point. Daily checks.",
	},
	{
		tool: "Peec AI",
		price: "Not published, quote on request",
		engines: "6 named",
		fit: "Mid-market teams wanting breadth.",
	},
	{
		tool: "Profound",
		price: "Custom, enterprise",
		engines: "Broad",
		fit: "Large brands and agencies. Deep source analytics.",
	},
	{
		tool: "VerifiedDR",
		price: "Free, then $39 / $99 / $199",
		engines: "3 (ChatGPT, Perplexity, Google AI Mode)",
		fit: "Teams wanting AI visibility and classic SEO in one place.",
	},
];

export function AiVisibilityToolsPost() {
	return (
		<div>
			<P>
				The question comes up in roughly this form: something is answering our
				customers now, and we have no idea whether it mentions us. Which tool
				fixes that?
			</P>
			<P>
				The category is about two years old and already crowded, so the honest
				answer depends less on which tool is best and more on which job you are
				actually doing. This piece covers what these tools measure, where they
				genuinely differ, and which one fits at four different budgets.
			</P>

			<H2>&gt; What these tools actually do</H2>
			<P>
				All of them work roughly the same way underneath. You give them a list
				of questions your buyers might ask. They ask those questions to AI
				engines on a schedule, read the answers, and record whether your brand
				appeared, whether it was recommended, and which pages the engine cited
				on the way.
			</P>
			<P>
				That last part matters more than the headline score. A mention tells you
				that you exist in the model's answer. The cited sources tell you why,
				and citations are the part you can act on, because they are usually
				pages you could get onto.
			</P>
			<Note>
				Be careful with any tool that reports a single visibility score with no
				way to see the raw answers. The score is a summary of a sample of
				generated text, and without the underlying answers you cannot tell a
				real change from model drift.
			</Note>

			<H2>&gt; Where they genuinely differ</H2>

			<H3>Engine coverage</H3>
			<P>
				The engines are not equally worth tracking, and coverage counts are the
				easiest number to inflate. ChatGPT, Google AI Overviews and AI Mode, and
				Perplexity are where the volume is for most businesses. Copilot, Gemini,
				Claude and Grok are real but usually secondary, and several tools bill
				them as add-ons rather than including them. A tool advertising eight
				engines may be charging you separately for five of them.
			</P>

			<H3>Frequency</H3>
			<P>
				Daily versus weekly checking sounds like a minor detail and is actually
				the main cost driver, because every check is a paid API call to a model.
				It is also the thing to match to reality. If you publish monthly, daily
				tracking mostly measures the model's own noise.
			</P>

			<H3>Prompt volume</H3>
			<P>
				Prompts are the real unit of pricing across the whole category. Compare
				tools on cost per prompt per month rather than on plan names, and be
				realistic about how many buyer questions you actually have. Most
				businesses have somewhere between ten and forty that matter.
			</P>

			<H2>&gt; The options, honestly</H2>
			<div className="mt-6 overflow-x-auto">
				<table className="w-full border border-border bg-card text-left">
					<thead>
						<tr className="border-border border-b">
							{["Tool", "Price", "Engines", "Best fit"].map((head) => (
								<th
									key={head}
									className="p-3 font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground"
								>
									{head}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{ROWS.map((row) => (
							<tr
								key={row.tool}
								className="border-border border-b last:border-0"
							>
								<td className="p-3 font-mono text-sm text-foreground">
									{row.tool}
								</td>
								<td className="p-3 text-sm text-muted-foreground">
									{row.price}
								</td>
								<td className="p-3 text-sm text-muted-foreground">
									{row.engines}
								</td>
								<td className="p-3 text-sm text-muted-foreground">{row.fit}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<H3>Otterly.AI</H3>
			<P>
				The cheapest way to find out whether you have a problem at all.{" "}
				<A href="https://otterly.ai/pricing/">Its published pricing</A> starts
				at $29 a month for 15 prompts across four engines, with Google AI Mode,
				Gemini and Claude sold as add-ons on top. Standard at $189 covers 100
				prompts and adds API and MCP access.
			</P>
			<P>
				The catch is the add-on structure. If the engines you care about are the
				extras, the real price is well above the headline. Worth pricing your
				actual engine list before comparing.
			</P>

			<H3>Peec AI</H3>
			<P>
				Positioned at mid-market teams and agencies, covering six engines
				including AI Mode, AI Overviews, Copilot and Gemini. Prices are not
				published on their pricing page, so treat any number you see quoted in a
				comparison post as secondhand and ask them directly.
			</P>

			<H3>Profound</H3>
			<P>
				The enterprise option, sold on custom pricing. The differentiator is
				depth of source analysis, meaning which publications and pages are
				shaping the answers about you across a large prompt set. If you are a
				brand with real search volume and a budget line for this, it is the
				serious pick. If you are a founder checking whether ChatGPT knows your
				product exists, it is not the right shape.
			</P>

			<H3>VerifiedDR</H3>
			<P>
				<A href="https://verifieddr.com/ai-visibility">VerifiedDR</A> tracks
				three platforms, ChatGPT, Perplexity and Google AI Mode, and shows every
				answer verbatim alongside which brands were named and in what tone.
				Every account gets one free scan of up to four questions, then Pro at
				$39 carries 25 questions per scan, Max at $99 carries 75, and Ultra at
				$199 carries 200.
			</P>
			<P>
				Two honest caveats. It covers three engines, not eight, so if Copilot or
				Claude coverage is a requirement it is the wrong tool. And the full
				refresh is weekly rather than daily, which is fine if you publish weekly
				and a limitation if you need same-day signal.
			</P>
			<P>
				What it does that the pure-play tools do not is keep AI visibility next
				to the classic authority metrics in the same product, so Domain Rating,
				backlinks, keyword positions and AI mentions sit on one site profile.
				That matters because the pages AI engines cite about you are usually the
				pages that already rank, and treating those as two separate problems in
				two separate tools is how the connection gets missed.
			</P>

			<H2>&gt; How to choose in about ten minutes</H2>
			<P>
				Write down the ten questions a buyer would actually type. Not keywords,
				questions. Then ask them yourself, by hand, in ChatGPT and Perplexity,
				and write down what came back.
			</P>
			<P>
				That exercise answers most of the purchasing question for free. If you
				are already named in most answers, you need monitoring and the cheapest
				credible tracker wins. If you are absent everywhere, you do not have a
				measurement problem yet, you have a coverage problem, and the tool worth
				paying for is the one that also tells you which sources to get onto.
			</P>

			<H2>&gt; Frequently asked questions</H2>

			<H3>Can I just check manually?</H3>
			<P>
				For a handful of questions once a quarter, yes, and you should at least
				once before buying anything. It stops working as a habit because answers
				vary between runs and between accounts, so a single manual check cannot
				separate a real change from noise. That variance is the actual argument
				for tooling.
			</P>

			<H3>Do these tools improve visibility or only measure it?</H3>
			<P>
				Mostly they measure. Some rank suggested actions on top. Be skeptical of
				any tool promising to get you into AI answers directly, because the
				mechanism is the same boring one as always, which is being covered and
				cited by sources the model already trusts.
			</P>

			<H3>Is this just SEO with a new name?</H3>
			<P>
				Substantially, yes, and that is the useful insight rather than a
				criticism. The engines cite pages that rank and publications with
				authority. What is genuinely new is that the answer is synthesized, so
				being cited without being clicked is now a normal outcome, and rank
				alone stops describing your position.
			</P>

			<H2>&gt; Why this shows up on AgentMRR</H2>
			<P>
				This board lists AI agents with revenue verified through their payment
				provider, so the recurring founder question here is how anyone discovers
				a product now. Increasingly the answer is that a model recommended it.
				Being absent from that answer is a distribution problem that looks like
				a marketing problem, and it is worth measuring before it is worth
				panicking about.
			</P>
		</div>
	);
}
